import type { Grouping, Platform } from "@aco/core";
import type { OrgSearch } from "@/lib/search-params";
import { clampSelectableReportingDay, getDatePreset } from "@/lib/utils";

// The reporting row's Date and Filter panels stage their edits and apply them
// in one step. Each turns what the person changed into one URL search patch,
// so applying is a single navigation.

export type ChangeDisplay = "off" | "absolute" | "percent";

/** What the Date panel applies: only the parts the person changed. */
export interface DateChange {
  range?: { preset: string; start: string; end: string };
  grouping?: Grouping;
  changeDisplay?: ChangeDisplay;
}

export function dateChangePatch(change: DateChange): Partial<OrgSearch> {
  const patch: Partial<OrgSearch> = {};
  if (change.range) {
    const { preset, start, end } = change.range;
    if (preset === "custom") {
      // Reporting is complete-days-only (issue 34): a custom range never
      // reaches past yesterday.
      Object.assign(patch, {
        preset,
        start: clampSelectableReportingDay(start),
        end: clampSelectableReportingDay(end),
      });
    } else {
      // A relative range resolves now and lets grouping follow it again.
      Object.assign(patch, { preset, ...getDatePreset(preset), grouping: undefined });
    }
  }
  if (change.grouping) patch.grouping = change.grouping;
  if (change.changeDisplay) {
    Object.assign(
      patch,
      change.changeDisplay === "off"
        ? { comparisonMode: "none", deltas: "hidden" }
        : { comparisonMode: "period", deltas: change.changeDisplay },
    );
  }
  return patch;
}

/** What the Filter panel applies: only the parts the person changed. */
export interface FilterChange {
  /** A Filter id, or "all" for all data. */
  segment?: string;
  platform?: "all" | Platform;
  breakdown?: string[];
  /** Breakdown results narrowing By campaign / By creative (remembered, not in the URL). */
  assignments?: Record<string, string[]>;
}

export function filterChangePatch(change: FilterChange): Partial<OrgSearch> {
  const patch: Partial<OrgSearch> = {};
  if (change.segment !== undefined) patch.segment = change.segment;
  if (change.platform !== undefined) patch.platform = change.platform;
  if (change.breakdown !== undefined) patch.breakdown = change.breakdown;
  return patch;
}
